import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { MultipartS3UploadService } from "src/app/shared/services/multipart-s3-upload.service";
import { VideoThumbnailService } from "src/app/shared/services/video-thumbnail.service";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import Swal from "sweetalert2";

/** Virtual scroll: row height (px) - image + padding + filename */
const PHOTO_ROW_HEIGHT = 150;
/** Columns in photo grid - match current media cards layout */
const PHOTO_GRID_COLUMNS = 4;
/** Enable virtual scroll when photo count exceeds this */
const VIRTUAL_SCROLL_THRESHOLD = 30;

@Component({
  selector: "app-tab-media",
  templateUrl: "./tab-media.component.html",
  styleUrls: ["./tab-media.component.scss"],
})
export class TabMediaComponent implements OnInit, OnChanges, OnDestroy {
  private readonly MAX_SUBSECTIONS_PER_SECTION = 12;
  /** First time API/model fills mediaSections after init — expand tree like Photos tab */
  private mediaSectionsHydrated = false;
  /** Cancel in-flight uploads when removing items or destroying the tab */
  private activeMediaUploads = new Map<string, Subscription>();
  private visibilityChangeHandler: (() => void) | null = null;
  private windowFocusHandler: (() => void) | null = null;
  /** Avoid duplicate URL thumbnail jobs for the same media row */
  private thumbnailFromUrlInFlight = new Set<string>();
  private readonly MAX_IMAGES_PER_SECTION = 1000;
  private readonly MAX_VIDEOS_PER_SECTION = 30;
  private readonly MAX_IMAGE_SIZE_MB = 20;
  private readonly MAX_VIDEO_SIZE_MB = 5120;

  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};
  @Input() allMediaSectionsExpanded: boolean = false;
  @Input() deleteSection: any[] = [];
  @Input() deleteSubSection: any[] = [];
  @Input() deleteMedia: any[] = [];

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();
  @Output() deleteSectionChange = new EventEmitter<any[]>();
  @Output() deleteSubSectionChange = new EventEmitter<any[]>();
  @Output() deleteMediaChange = new EventEmitter<any[]>();

  readonly photoItemSize = PHOTO_ROW_HEIGHT;
  readonly virtualScrollThreshold = VIRTUAL_SCROLL_THRESHOLD;

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private multipartUploadService: MultipartS3UploadService,
    private videoThumbnailService: VideoThumbnailService,
    private heicConverter: HeicConverterService
  ) {}

  ngOnDestroy(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener("visibilitychange", this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    if (this.windowFocusHandler) {
      window.removeEventListener("focus", this.windowFocusHandler);
      this.windowFocusHandler = null;
    }
    this.thumbnailFromUrlInFlight.clear();
    this.activeMediaUploads.forEach((sub, uploadKey) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
      this.cleanupUpload(uploadKey);
    });
    this.removePendingMediaWithoutUrl();
    this.activeMediaUploads.clear();
  }

  get maxSubsectionsPerSection(): number {
    return this.MAX_SUBSECTIONS_PER_SECTION;
  }

  ngOnInit(): void {
    this.ensureMediaSectionsShape();
    if (this.model.mediaSections.length > 0) {
      this.expandAllSectionsForEditing();
      this.mediaSectionsHydrated = true;
    }
    this.allMediaSectionsExpanded = true;
    this.updateMainDisplayCheckbox();

    // Re-run thumbnail recovery after user returns to tab/window.
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === "visible") {
        this.retryMissingVideoThumbnails();
      }
    };
    this.windowFocusHandler = () => this.retryMissingVideoThumbnails();
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);
    window.addEventListener("focus", this.windowFocusHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["model"]) {
      return;
    }
    this.ensureMediaSectionsShape();
    const len = this.model.mediaSections.length;
    if (len === 0) {
      this.mediaSectionsHydrated = false;
      return;
    }
    if (!this.mediaSectionsHydrated) {
      this.expandAllSectionsForEditing();
      this.mediaSectionsHydrated = true;
      this.allMediaSectionsExpanded = true;
      this.updateMainDisplayCheckbox();
    }
  }

  /** Defaults + indexes only (does not change collapsed) */
  private ensureMediaSectionsShape(): void {
    if (!this.model.mediaSections) {
      this.model.mediaSections = [];
    }
    this.model.mediaSections.forEach((section, index) => {
      if (section.display === undefined) section.display = true;
      if (section.index === undefined) section.index = index;
      section.subSections = section.subSections || [];
      section.subSections.forEach((sub, subIndex) => {
        if (sub.display === undefined) sub.display = true;
        if (sub.index === undefined) sub.index = subIndex;
        sub.media = sub.media || [];
      });
    });
  }

  /** Match tab-photos: open all sections/subsections in the editor so fields/uploads are visible */
  private expandAllSectionsForEditing(): void {
    this.model.mediaSections.forEach((section, index) => {
      section.collapsed = false;
      if (section.display === undefined) section.display = true;
      if (section.index === undefined) section.index = index;
      section.subSections = section.subSections || [];
      section.subSections.forEach((sub, subIndex) => {
        sub.collapsed = false;
        if (sub.display === undefined) sub.display = true;
        if (sub.index === undefined) sub.index = subIndex;
        sub.media = sub.media || [];
      });
    });
  }

  checkAndSetMediaDisplay() {
    if (!this.model.displayMedias) {
      const hasContent =
        !!this.model.titleMediaSections ||
        (this.model.mediaSections || []).some(
          section =>
            !!section.name ||
            (section.subSections || []).some(
              sub => !!sub.title || !!sub.pageTitle || !!sub.description || (sub.media || []).length > 0
            )
        );
      if (hasContent) {
        this.model.displayMedias = true;
      }
    }
    this.modelChange.emit(this.model);
  }

  addMediaSection() {
    this.model.mediaSections = this.model.mediaSections || [];
    const sectionDisplay = !!this.model.displayMedias;
    const nextSectionIndex =
      this.model.mediaSections.length === 0 ? 0 : Math.max(...this.model.mediaSections.map(s => s.index || 0)) + 1;
    this.model.mediaSections.push({
      name: "",
      collapsed: false,
      display: sectionDisplay,
      index: nextSectionIndex,
      subSections: [
        {
          title: "",
          pageTitle: "",
          description: "",
          media: [],
          collapsed: false,
          display: sectionDisplay,
          index: 0,
        },
      ],
    });
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deleteMediaSection(sectionIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the entire section and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (!result.value) return;
      const removed = this.model.mediaSections[sectionIndex];
      if (removed && removed.id) {
        this.deleteSection.push(removed.id);
        this.deleteSectionChange.emit(this.deleteSection);
      }
      this.model.mediaSections.splice(sectionIndex, 1);
      this.modelChange.emit(this.model);
    });
  }

  addMediaSubsection(sectionIndex: number) {
    const section = this.model.mediaSections[sectionIndex];
    if (section.subSections.length >= this.MAX_SUBSECTIONS_PER_SECTION) {
      this.toastr.error(`Maximum ${this.MAX_SUBSECTIONS_PER_SECTION} sub-sections allowed per section`);
      return;
    }
    const nextSubIndex =
      section.subSections.length === 0 ? 0 : Math.max(...section.subSections.map(ss => ss.index || 0)) + 1;
    section.subSections.push({
      title: "",
      pageTitle: "",
      description: "",
      media: [],
      collapsed: false,
      display: !!section.display,
      index: nextSubIndex,
    });
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deleteMediaSubsection(sectionIndex: number, subIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the subsection and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (!result.value) return;
      const removed = this.model.mediaSections[sectionIndex].subSections[subIndex];
      if (removed && removed.id) {
        this.deleteSubSection.push(removed.id);
        this.deleteSubSectionChange.emit(this.deleteSubSection);
      }
      this.model.mediaSections[sectionIndex].subSections.splice(subIndex, 1);
      this.modelChange.emit(this.model);
    });
  }

  toggleSectionCollapse(section: any) {
    section.collapsed = !section.collapsed;
  }

  toggleSubsectionCollapse(subsection: any) {
    subsection.collapsed = !subsection.collapsed;
  }

  onDisplayMediaChange(value: boolean) {
    this.model.displayMedias = value;
    (this.model.mediaSections || []).forEach(section => {
      section.display = value;
      (section.subSections || []).forEach(sub => (sub.display = value));
    });
    this.modelChange.emit(this.model);
  }

  onSectionDisplayChange(sectionIndex: number, value: boolean) {
    const section = this.model.mediaSections[sectionIndex];
    section.display = value;
    (section.subSections || []).forEach(sub => (sub.display = value));
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  onSubsectionDisplayChange(sectionIndex: number, subIndex: number, value: boolean) {
    const section = this.model.mediaSections[sectionIndex];
    const subsection = section && section.subSections ? section.subSections[subIndex] : null;
    if (!subsection) return;
    subsection.display = value;
    if (value) {
      section.display = true;
    } else if (!section.subSections.some(sub => sub.display === true)) {
      section.display = false;
    }
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  private updateMainDisplayCheckbox() {
    const sections = this.model.mediaSections || [];
    if (sections.length === 0) {
      if (this.model.displayMedias === false) this.model.displayMedias = false;
      return;
    }
    const hasAnyChecked = sections.some(section => section.display === true);
    if (this.model.displayMedias !== hasAnyChecked) {
      this.model.displayMedias = hasAnyChecked;
      this.modelChange.emit(this.model);
    }
  }

  dropSection(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.model.mediaSections, event.previousIndex, event.currentIndex);
    this.model.mediaSections.forEach((section, index) => (section.index = index));
    this.modelChange.emit(this.model);
  }

  moveSectionUp(sectionIndex: number) {
    if (sectionIndex <= 0) return;
    const sections = this.model.mediaSections;
    [sections[sectionIndex - 1], sections[sectionIndex]] = [sections[sectionIndex], sections[sectionIndex - 1]];
    sections.forEach((section, index) => (section.index = index));
    this.modelChange.emit(this.model);
  }

  moveSectionDown(sectionIndex: number) {
    const sections = this.model.mediaSections;
    if (sectionIndex >= sections.length - 1) return;
    [sections[sectionIndex], sections[sectionIndex + 1]] = [sections[sectionIndex + 1], sections[sectionIndex]];
    sections.forEach((section, index) => (section.index = index));
    this.modelChange.emit(this.model);
  }

  moveSubsectionUp(sectionIndex: number, subIndex: number) {
    const subSections = this.model.mediaSections[sectionIndex].subSections;
    if (subIndex <= 0) return;
    [subSections[subIndex - 1], subSections[subIndex]] = [subSections[subIndex], subSections[subIndex - 1]];
    subSections.forEach((sub, index) => (sub.index = index));
    this.modelChange.emit(this.model);
  }

  moveSubsectionDown(sectionIndex: number, subIndex: number) {
    const subSections = this.model.mediaSections[sectionIndex].subSections;
    if (subIndex >= subSections.length - 1) return;
    [subSections[subIndex], subSections[subIndex + 1]] = [subSections[subIndex + 1], subSections[subIndex]];
    subSections.forEach((sub, index) => (sub.index = index));
    this.modelChange.emit(this.model);
  }

  toggleExpandAllMediaSections() {
    this.allMediaSectionsExpanded = !this.allMediaSectionsExpanded;
    this.model.mediaSections.forEach(section => {
      section.collapsed = !this.allMediaSectionsExpanded;
      section.subSections.forEach(sub => (sub.collapsed = !this.allMediaSectionsExpanded));
    });
  }

  async uploadPhotosToSubsection(files: FileList, sectionIndex: number, subIndex: number, event?: Event) {
    const reset = () => {
      const target = event && (event.target as HTMLInputElement);
      if (target) target.value = "";
    };
    if (!files || files.length === 0) return;
    const subsection = this.model.mediaSections[sectionIndex].subSections[subIndex];
    subsection.media = subsection.media || [];

    const entries = Array.from(files).sort((a, b) => a.name.localeCompare(b.name));
    for (const rawFile of entries) {
      const acceptedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
      const isHeicByExtension = /\.(heic|heif)$/i.test(rawFile.name);
      const isImageType = acceptedImageTypes.includes((rawFile.type || "").toLowerCase()) || isHeicByExtension;

      if (!isImageType) {
        this.toastr.error(`File ${rawFile.name} format not supported. Accepted formats: JPEG, JPG, PNG, WEBP, HEIC`);
        continue;
      }
      if (rawFile.size / 1024 / 1024 > this.MAX_IMAGE_SIZE_MB) {
        this.toastr.error(`File ${rawFile.name} exceeds ${this.MAX_IMAGE_SIZE_MB}MB`);
        continue;
      }
      if (this.getImageCountInSection(sectionIndex) >= this.MAX_IMAGES_PER_SECTION) {
        this.toastr.error(`Maximum ${this.MAX_IMAGES_PER_SECTION} photos per section reached`);
        continue;
      }

      const file = await this.heicConverter.convertToJpegIfNeeded(rawFile);
      const timestamp = Date.now();
      const uploadKey = this.generateUploadKey("mediaPhoto", file.name, `${sectionIndex}_${subIndex}_${timestamp}`);
      const tempId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
      this.setUploadState(uploadKey, true);
      const mediaItem: any = {
        fileName: file.name,
        url: null,
        size: file.size,
        type: "image",
        uploadKey,
        tempId,
        isPending: true,
        imageLoading: false,
        thumbnail: null,
      };
      subsection.media.push(mediaItem);
      this.modelChange.emit(this.model);
      await this.uploadSinglePhoto(file, subsection, mediaItem, uploadKey);
    }
    subsection.media.sort((a, b) => (a.fileName || "").localeCompare(b.fileName || ""));
    this.checkAndSetMediaDisplay();
    reset();
  }

  async uploadMediaToSubsection(files: FileList, sectionIndex: number, subIndex: number, event?: Event) {
    const reset = () => {
      const target = event && (event.target as HTMLInputElement);
      if (target) target.value = "";
    };
    if (!files || files.length === 0) return;

    const subsection = this.model.mediaSections[sectionIndex].subSections[subIndex];
    subsection.media = subsection.media || [];
    const entries = Array.from(files).sort((a, b) => a.name.localeCompare(b.name));

    for (const rawFile of entries) {
      const isImage = this.isImageFile(rawFile);
      const isVideo = this.isVideoFile(rawFile);

      if (!isImage && !isVideo) {
        this.toastr.error(
          `File ${rawFile.name} format not supported. Accepted formats: image (JPEG, PNG, WEBP, HEIC) or video (MP4, AVI, MOV, WMV, FLV, MKV, WEBM)`
        );
        continue;
      }

      if (isImage) {
        if (rawFile.size / 1024 / 1024 > this.MAX_IMAGE_SIZE_MB) {
          this.toastr.error(`File ${rawFile.name} exceeds ${this.MAX_IMAGE_SIZE_MB}MB`);
          continue;
        }
        if (this.getImageCountInSection(sectionIndex) >= this.MAX_IMAGES_PER_SECTION) {
          this.toastr.error(`Maximum ${this.MAX_IMAGES_PER_SECTION} photos per section reached`);
          continue;
        }

        const file = await this.heicConverter.convertToJpegIfNeeded(rawFile);
        const timestamp = Date.now();
        const uploadKey = this.generateUploadKey("mediaPhoto", file.name, `${sectionIndex}_${subIndex}_${timestamp}`);
        const tempId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
        this.setUploadState(uploadKey, true);
        const mediaItem: any = {
          fileName: file.name,
          url: null,
          size: file.size,
          type: "image",
          uploadKey,
          tempId,
          isPending: true,
          imageLoading: false,
          thumbnail: null,
        };
        subsection.media.push(mediaItem);
        this.modelChange.emit(this.model);
        await this.uploadSinglePhoto(file, subsection, mediaItem, uploadKey);
        continue;
      }

      if (rawFile.size / 1024 / 1024 > this.MAX_VIDEO_SIZE_MB) {
        this.toastr.error(`File ${rawFile.name} exceeds ${this.MAX_VIDEO_SIZE_MB}MB`);
        continue;
      }
      if (this.getVideoCountInSection(sectionIndex) >= this.MAX_VIDEOS_PER_SECTION) {
        this.toastr.error(`Maximum ${this.MAX_VIDEOS_PER_SECTION} videos per section reached`);
        continue;
      }

      const timestamp = Date.now();
      const uploadKey = this.generateUploadKey("mediaVideo", rawFile.name, `${sectionIndex}_${subIndex}_${timestamp}`);
      const tempId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
      this.setUploadState(uploadKey, true);
      const mediaItem: any = {
        fileName: rawFile.name,
        url: null,
        size: rawFile.size,
        type: "video",
        uploadKey,
        tempId,
        mediaClientKey: `mc_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
        thumbnail: null,
        isPending: true,
      };
      subsection.media.push(mediaItem);
      this.modelChange.emit(this.model);
      const thumbnailHeadStart = this.startVideoThumbnailUpload(rawFile, subsection, mediaItem);
      await Promise.race([thumbnailHeadStart, this.waitMs(8000)]);
      await this.uploadSingleVideoMultipart(rawFile, subsection, mediaItem, uploadKey);
    }

    subsection.media.sort((a, b) => (a.fileName || "").localeCompare(b.fileName || ""));
    this.checkAndSetMediaDisplay();
    reset();
  }

  async uploadVideosToSubsection(files: FileList, sectionIndex: number, subIndex: number, event?: Event) {
    const reset = () => {
      const target = event && (event.target as HTMLInputElement);
      if (target) target.value = "";
    };
    if (!files || files.length === 0) return;
    const subsection = this.model.mediaSections[sectionIndex].subSections[subIndex];
    subsection.media = subsection.media || [];

    const entries = Array.from(files).sort((a, b) => a.name.localeCompare(b.name));
    const acceptedVideoTypes = [
      "video/mp4",
      "video/avi",
      "video/x-msvideo",
      "video/quicktime",
      "video/x-ms-wmv",
      "video/x-flv",
      "video/x-matroska",
      "video/mkv",
      "video/webm",
    ];
    for (const file of entries) {
      const isValidVideoType = acceptedVideoTypes.includes((file.type || "").toLowerCase()) || this.isVideoFile(file);
      if (!isValidVideoType) {
        this.toastr.error(
          `File ${file.name} format not supported. Accepted formats: MP4, AVI, MOV, WMV, FLV, MKV, WEBM`
        );
        continue;
      }
      if (file.size / 1024 / 1024 > this.MAX_VIDEO_SIZE_MB) {
        this.toastr.error(`File ${file.name} exceeds ${this.MAX_VIDEO_SIZE_MB}MB`);
        continue;
      }
      if (this.getVideoCountInSection(sectionIndex) >= this.MAX_VIDEOS_PER_SECTION) {
        this.toastr.error(`Maximum ${this.MAX_VIDEOS_PER_SECTION} videos per section reached`);
        continue;
      }

      const timestamp = Date.now();
      const uploadKey = this.generateUploadKey("mediaVideo", file.name, `${sectionIndex}_${subIndex}_${timestamp}`);
      const tempId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
      this.setUploadState(uploadKey, true);
      const mediaItem: any = {
        fileName: file.name,
        url: null,
        size: file.size,
        type: "video",
        uploadKey,
        tempId,
        mediaClientKey: `mc_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
        thumbnail: null,
        isPending: true,
      };
      subsection.media.push(mediaItem);
      this.modelChange.emit(this.model);
      const thumbnailHeadStart = this.startVideoThumbnailUpload(file, subsection, mediaItem);
      await Promise.race([thumbnailHeadStart, this.waitMs(8000)]);
      await this.uploadSingleVideoMultipart(file, subsection, mediaItem, uploadKey);
    }
    subsection.media.sort((a, b) => (a.fileName || "").localeCompare(b.fileName || ""));
    this.checkAndSetMediaDisplay();
    reset();
  }

  private isVideoFile(file: File): boolean {
    if (file.type.startsWith("video/")) {
      return true;
    }
    return /\.(mp4|webm|avi|mov|wmv|mkv|m4v)$/i.test(file.name);
  }

  private isImageFile(file: File): boolean {
    const acceptedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    return acceptedImageTypes.includes((file.type || "").toLowerCase()) || isHeicByExtension;
  }

  private findVideoMediaIndex(subsection: any, seed: any): number {
    if (!subsection || !subsection.media || !seed) {
      return -1;
    }
    if (seed.mediaClientKey) {
      const byKey = subsection.media.findIndex((m: any) => m && m.mediaClientKey === seed.mediaClientKey);
      if (byKey !== -1) return byKey;
    }
    if (seed.tempId) {
      const byTempId = subsection.media.findIndex((m: any) => m && m.tempId === seed.tempId);
      if (byTempId !== -1) return byTempId;
    }
    if (seed.uploadKey) {
      const byUploadKey = subsection.media.findIndex((m: any) => m && m.uploadKey === seed.uploadKey);
      if (byUploadKey !== -1) return byUploadKey;
    }
    if (seed.fileName) {
      const byFileName = subsection.media.findIndex(
        (m: any) => m && this.isVideoMedia(m) && m.fileName === seed.fileName
      );
      if (byFileName !== -1) return byFileName;
    }
    if (seed.url) {
      const byUrl = subsection.media.findIndex((m: any) => m && this.isVideoMedia(m) && m.url === seed.url);
      if (byUrl !== -1) return byUrl;
    }
    return -1;
  }

  private startVideoThumbnailUpload(file: File, subsection: any, mediaItem: any): Promise<void> {
    const thumbKey = `${mediaItem.uploadKey}_thumb`;
    mediaItem.thumbnailProcessing = true;
    mediaItem.needsThumbnailRetry = false;
    return new Promise(resolve => {
      let resolved = false;
      const safeResolve = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };
      const sub = this.videoThumbnailService.generateThumbnailFromVideo(file, "digital/thumb", 5).subscribe({
        next: (response: any) => {
          if (!response) {
            return;
          }
          const index = this.findVideoMediaIndex(subsection, mediaItem);
          if (index === -1) {
            return;
          }

          if (response.type === "preview" && response.url) {
            subsection.media[index] = {
              ...subsection.media[index],
              thumbnail: response.url,
              thumbnailProcessing: true,
              needsThumbnailRetry: false,
            };
            this.modelChange.emit(this.model);
            return;
          }

          if (response.type === "complete") {
            const prev = subsection.media[index];
            const nextThumb = response.url || prev.thumbnail || null;
            const merged = { ...prev, thumbnail: nextThumb };
            subsection.media[index] = {
              ...merged,
              thumbnailProcessing: false,
              needsThumbnailRetry: !this.hasDisplayableThumbnail(merged),
            };
            this.modelChange.emit(this.model);
            safeResolve();
          }
        },
        error: () => {
          const index = this.findVideoMediaIndex(subsection, mediaItem);
          if (index !== -1) {
            subsection.media[index] = {
              ...subsection.media[index],
              thumbnailProcessing: false,
              needsThumbnailRetry: true,
            };
            this.modelChange.emit(this.model);
          }
          safeResolve();
        },
        complete: () => {
          this.activeMediaUploads.delete(thumbKey);
          safeResolve();
        },
      });
      this.activeMediaUploads.set(thumbKey, sub);
    });
  }

  private async uploadSinglePhoto(file: File, subsection: any, mediaItem: any, uploadKey: string): Promise<void> {
    return new Promise<void>(resolve => {
      const sub = this.uploadService.uploadFileToS3("digital/photos", file, this.MAX_IMAGE_SIZE_MB).subscribe({
        next: (response: any) => {
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            return;
          }
          if (response && response.type === "complete") {
            this.setUploadState(uploadKey, false);
            const index = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
            if (index !== -1) {
              subsection.media[index] = {
                ...subsection.media[index],
                url: response.url,
                uploadKey: null,
                isPending: false,
                imageLoading: true,
              };
              delete subsection.media[index].tempId;
            }
            this.cleanupUpload(uploadKey);
            this.activeMediaUploads.delete(uploadKey);
            this.modelChange.emit(this.model);
            resolve();
          }
        },
        error: error => {
          this.setUploadState(uploadKey, false);
          const index = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
          if (index !== -1) subsection.media.splice(index, 1);
          this.cleanupUpload(uploadKey);
          this.activeMediaUploads.delete(uploadKey);
          this.toastr.error(`Failed to upload ${file.name}: ${error && error.message ? error.message : error}`);
          this.modelChange.emit(this.model);
          resolve();
        },
      });
      this.activeMediaUploads.set(uploadKey, sub);
    });
  }

  private async uploadSingleVideoMultipart(
    file: File,
    subsection: any,
    mediaItem: any,
    uploadKey: string
  ): Promise<void> {
    return new Promise<void>(resolve => {
      const sub = this.multipartUploadService
        .uploadVideoMultipart("digital/videos", file, {
          maxSizeMB: this.MAX_VIDEO_SIZE_MB,
          chunkSizeMB: 15,
          fileId: uploadKey,
        })
        .subscribe({
          next: (response: any) => {
            if (response && response.type === "progress") {
              this.setUploadProgress(uploadKey, response.percent);
              return;
            }
            if (response && response.type === "complete") {
              this.setUploadState(uploadKey, false);
              const index = this.findVideoMediaIndex(subsection, mediaItem);
              if (index !== -1) {
                subsection.media[index] = {
                  ...subsection.media[index],
                  url: response.url,
                  uploadKey: null,
                  isPending: false,
                };
                if (!this.hasDisplayableThumbnail(subsection.media[index])) {
                  this.generateThumbnailFromUploadedUrl(response.url, subsection, mediaItem);
                }
                delete subsection.media[index].tempId;
              }
              this.cleanupUpload(uploadKey);
              this.activeMediaUploads.delete(uploadKey);
              this.modelChange.emit(this.model);
              resolve();
            }
          },
          error: error => {
            if (error && error.message && String(error.message).includes("cancelled")) {
              this.setUploadState(uploadKey, false);
              const index = this.findVideoMediaIndex(subsection, mediaItem);
              if (index !== -1) {
                subsection.media.splice(index, 1);
              }
              this.cleanupUpload(uploadKey);
              this.activeMediaUploads.delete(uploadKey);
              this.modelChange.emit(this.model);
              resolve();
              return;
            }
            this.setUploadState(uploadKey, false);
            const index = this.findVideoMediaIndex(subsection, mediaItem);
            if (index !== -1) {
              subsection.media.splice(index, 1);
            }
            this.cleanupUpload(uploadKey);
            this.activeMediaUploads.delete(uploadKey);
            this.toastr.error(`Failed to upload ${file.name}: ${error && error.message ? error.message : error}`);
            this.modelChange.emit(this.model);
            resolve();
          },
        });
      this.activeMediaUploads.set(uploadKey, sub);
    });
  }

  hasDisplayableThumbnail(media: any): boolean {
    const t = media && media.thumbnail;
    if (typeof t !== "string") {
      return false;
    }
    const normalized = t.trim();
    // `data:` thumbnails are temporary preview snapshots and are not reliable after save/refresh.
    return normalized !== "" && normalized !== "data:," && !normalized.startsWith("data:");
  }

  canRetryThumbnail(media: any): boolean {
    if (!media || media.type !== "video") {
      return false;
    }
    if (this.isPublished()) {
      return false;
    }
    if (media.isPending === true) {
      return false;
    }
    if (media.uploadKey) {
      return false;
    }
    if (media.uploadKey && this.isUploadInProgress(media.uploadKey)) {
      return false;
    }
    if (media.thumbnailProcessing) {
      return false;
    }
    if (!this.hasPlayableVideoUrl(media) || this.isBlobUrl(media.url)) {
      return false;
    }
    if (this.hasDisplayableThumbnail(media)) {
      return false;
    }
    return media.needsThumbnailRetry === true;
  }

  isPublished(): boolean {
    return !!(this.model && this.model.published === true);
  }

  hasMissingThumbnail(media: any): boolean {
    if (!this.isVideoMedia(media)) {
      return false;
    }
    if (media && media.uploadKey && this.isUploadInProgress(media.uploadKey)) {
      return false;
    }
    if (media && media.thumbnailProcessing) {
      return false;
    }
    return !this.hasDisplayableThumbnail(media);
  }

  getMissingThumbnailCount(subsection: any): number {
    if (!subsection || !subsection.media) {
      return 0;
    }
    return subsection.media.filter((media: any) => this.hasMissingThumbnail(media)).length;
  }

  private isNewUploadedVideo(media: any): boolean {
    if (!this.isVideoMedia(media)) {
      return false;
    }
    const mediaId = Number(media && media.id);
    return !Number.isFinite(mediaId) || mediaId <= 0;
  }

  getMissingThumbnailCountForNewUploads(subsection: any): number {
    if (!subsection || !subsection.media) {
      return 0;
    }
    return subsection.media.filter((media: any) => this.shouldWarnMissingThumbnail(media)).length;
  }

  shouldWarnMissingThumbnail(media: any): boolean {
    if (!this.isNewUploadedVideo(media)) {
      return false;
    }
    if (!media || !media.url || media.isPending === true) {
      return false;
    }
    if (media.thumbnailProcessing === true) {
      return false;
    }
    if (this.hasDisplayableThumbnail(media)) {
      return false;
    }
    return media.needsThumbnailRetry === true;
  }

  getMissingThumbnailWarningMessage(subsection: any): string {
    const count = this.getMissingThumbnailCountForNewUploads(subsection);
    if (count <= 0) {
      return "";
    }
    return `${count} newly uploaded video(s) failed thumbnail generation and need retry. If not retried, these videos may be shown without thumbnail.`;
  }

  retryThumbnailForMedia(media: any, showToast: boolean = true, videoElement?: HTMLVideoElement | null): void {
    if (this.isPublished()) {
      if (showToast) {
        this.toastr.info("This log is already published, so thumbnail retry is disabled.");
      }
      return;
    }
    if (!this.canRetryThumbnail(media)) {
      return;
    }
    media.thumbnailProcessing = true;
    media.needsThumbnailRetry = true;
    this.modelChange.emit(this.model);

    this.ensureMediaClientKeyForRow(media);
    const startRetryUpload = () =>
      this.ensureThumbnailFromUploadedUrl(media.url, media.fileName, media.mediaClientKey, showToast)
        .then(() => {
          if (!this.hasDisplayableThumbnail(media)) {
            media.thumbnailProcessing = false;
          }
          this.modelChange.emit(this.model);
        })
        .catch(() => {
          media.thumbnailProcessing = false;
          this.modelChange.emit(this.model);
        });

    this.warmupVideoBeforeThumbnailRetry(videoElement)
      .then(() => void startRetryUpload())
      .catch(() => void startRetryUpload());
  }

  private async warmupVideoBeforeThumbnailRetry(videoElement?: HTMLVideoElement | null): Promise<void> {
    if (!videoElement) {
      return;
    }

    try {
      if (videoElement.readyState < 2) {
        await new Promise<void>(resolve => {
          const onLoadedData = () => {
            cleanup();
            resolve();
          };
          const onError = () => {
            cleanup();
            resolve();
          };
          const timer = setTimeout(() => {
            cleanup();
            resolve();
          }, 1200);
          const cleanup = () => {
            clearTimeout(timer);
            videoElement.removeEventListener("loadeddata", onLoadedData);
            videoElement.removeEventListener("error", onError);
          };
          videoElement.addEventListener("loadeddata", onLoadedData, { once: true });
          videoElement.addEventListener("error", onError, { once: true });
        });
      }

      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.then === "function") {
        await playPromise.catch(() => undefined);
      }

      await new Promise(resolve => setTimeout(resolve, 400));
      videoElement.pause();
    } catch (_e) {
      // Best effort warmup only; retry flow continues even if playback is blocked.
    }
  }

  private isBlobUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return String(url).startsWith("blob:");
  }

  private hasPlayableVideoUrl(media: any): boolean {
    if (!media || !media.url) {
      return false;
    }
    return String(media.url).trim().length > 0;
  }

  /** API may omit type for images; this tab only supports photo + video */
  private isPhotoLikeMedia(media: any): boolean {
    if (!media) return false;
    return String(media.type || "").toLowerCase() !== "video";
  }

  private isVideoMedia(media: any): boolean {
    if (!media) return false;
    return String(media.type || "").toLowerCase() === "video";
  }

  getPhotoUploadText(subsection: any): string {
    const photos = (subsection.media || []).filter((m: any) => this.isPhotoLikeMedia(m)).length;
    if (photos === 0) {
      return "Select Photos";
    }
    return photos === 1 ? "1 photo selected" : `${photos} photos selected`;
  }

  getVideoUploadText(subsection: any): string {
    const videos = (subsection.media || []).filter((m: any) => this.isVideoMedia(m)).length;
    if (videos === 0) {
      return "Select Videos";
    }
    return videos === 1 ? "1 video selected" : `${videos} videos selected`;
  }

  getMediaUploadText(subsection: any): string {
    const images = (subsection.media || []).filter((m: any) => this.isPhotoLikeMedia(m)).length;
    const videos = (subsection.media || []).filter((m: any) => this.isVideoMedia(m)).length;
    const total = images + videos;
    if (total === 0) {
      return "Select Media";
    }
    if (images > 0 && videos > 0) {
      return `${images} photos, ${videos} videos selected`;
    }
    if (images > 0) {
      return images === 1 ? "1 photo selected" : `${images} photos selected`;
    }
    return videos === 1 ? "1 video selected" : `${videos} videos selected`;
  }

  getVideoRows(subsection: any): { media: any; mediaIndex: number }[] {
    if (!subsection || !subsection.media) return [];
    const rows: { media: any; mediaIndex: number }[] = [];
    subsection.media.forEach((media: any, mediaIndex: number) => {
      if (this.isVideoMedia(media)) {
        rows.push({ media, mediaIndex });
      }
    });
    return rows;
  }

  trackByVideoRow(_index: number, row: { media: any; mediaIndex: number }): number {
    return row.mediaIndex;
  }

  removeMediaFromSubsection(sectionIndex: number, subIndex: number, mediaIndex: number) {
    const subsection = this.model.mediaSections[sectionIndex].subSections[subIndex];
    const media = subsection.media[mediaIndex];
    if (media && media.uploadKey) {
      const main = this.activeMediaUploads.get(media.uploadKey);
      if (main && !main.closed) {
        main.unsubscribe();
      }
      this.activeMediaUploads.delete(media.uploadKey);
      const thumbKey = `${media.uploadKey}_thumb`;
      const thumb = this.activeMediaUploads.get(thumbKey);
      if (thumb && !thumb.closed) {
        thumb.unsubscribe();
      }
      this.activeMediaUploads.delete(thumbKey);
      this.cleanupUpload(media.uploadKey);
    }
    if (media && media.id) {
      this.deleteMedia.push(media.id);
      this.deleteMediaChange.emit(this.deleteMedia);
    }
    subsection.media.splice(mediaIndex, 1);
    this.modelChange.emit(this.model);
  }

  deleteAllPhotosFromSubsection(sectionIndex: number, subIndex: number): void {
    this.deleteAllMediaMatchingFromSubsection(sectionIndex, subIndex, m => this.isPhotoLikeMedia(m), "photo(s)");
  }

  deleteAllVideosFromSubsection(sectionIndex: number, subIndex: number): void {
    this.deleteAllMediaMatchingFromSubsection(sectionIndex, subIndex, m => this.isVideoMedia(m), "video(s)");
  }

  deleteAllMediaFromSubsection(sectionIndex: number, subIndex: number) {
    const subsection = this.model.mediaSections[sectionIndex].subSections[subIndex];
    if (!subsection || !subsection.media || subsection.media.length === 0) {
      this.toastr.info("No media to delete");
      return;
    }
    const count = subsection.media.length;
    Swal.fire({
      title: "Are you sure?",
      text: `This will delete all ${count} media file(s) from this sub-section.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (!result.value) return;
      subsection.media.forEach((m: any) => {
        if (m && m.uploadKey) {
          const main = this.activeMediaUploads.get(m.uploadKey);
          if (main && !main.closed) main.unsubscribe();
          this.activeMediaUploads.delete(m.uploadKey);
          const thumbKey = `${m.uploadKey}_thumb`;
          const thumb = this.activeMediaUploads.get(thumbKey);
          if (thumb && !thumb.closed) thumb.unsubscribe();
          this.activeMediaUploads.delete(thumbKey);
          this.cleanupUpload(m.uploadKey);
        }
      });
      const mediaIdsToDelete = subsection.media
        .filter(media => media.id && typeof media.id === "number" && media.id > 0)
        .map(media => media.id);
      if (mediaIdsToDelete.length > 0) {
        this.deleteMedia = this.deleteMedia.concat(mediaIdsToDelete);
        this.deleteMediaChange.emit(this.deleteMedia);
      }
      subsection.media = [];
      this.modelChange.emit(this.model);
    });
  }

  private deleteAllMediaMatchingFromSubsection(
    sectionIndex: number,
    subIndex: number,
    predicate: (m: any) => boolean,
    label: string
  ): void {
    const subsection = this.model.mediaSections[sectionIndex].subSections[subIndex];
    if (!subsection || !subsection.media || subsection.media.length === 0) {
      this.toastr.info(`No ${label} to delete`);
      return;
    }

    const mediasToDelete = subsection.media.filter(predicate);
    if (mediasToDelete.length === 0) {
      this.toastr.info(`No ${label} to delete`);
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `This will delete all ${mediasToDelete.length} ${label} from this sub-section.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (!result.value) return;

      mediasToDelete.forEach((m: any) => {
        if (m && m.uploadKey) {
          const main = this.activeMediaUploads.get(m.uploadKey);
          if (main && !main.closed) main.unsubscribe();
          this.activeMediaUploads.delete(m.uploadKey);

          const thumbKey = `${m.uploadKey}_thumb`;
          const thumb = this.activeMediaUploads.get(thumbKey);
          if (thumb && !thumb.closed) thumb.unsubscribe();
          this.activeMediaUploads.delete(thumbKey);
          this.cleanupUpload(m.uploadKey);
        }
      });

      const mediaIdsToDelete = mediasToDelete
        .filter((media: any) => media.id && typeof media.id === "number" && media.id > 0)
        .map((media: any) => media.id);
      if (mediaIdsToDelete.length > 0) {
        this.deleteMedia = this.deleteMedia.concat(mediaIdsToDelete);
        this.deleteMediaChange.emit(this.deleteMedia);
      }

      subsection.media = subsection.media.filter((m: any) => !predicate(m));
      this.modelChange.emit(this.model);
    });
  }

  getPhotoRows(subsection: any): { media: any; mediaIndex: number }[] {
    if (!subsection || !subsection.media) return [];
    const rows: { media: any; mediaIndex: number }[] = [];
    subsection.media.forEach((media: any, mediaIndex: number) => {
      if (this.isPhotoLikeMedia(media)) {
        rows.push({ media, mediaIndex });
      }
    });
    return rows;
  }

  trackByPhotoRow(_index: number, row: { media: any; mediaIndex: number }): number {
    return row.mediaIndex;
  }

  /** Stable trackBy for photo cards - avoids excessive DOM churn */
  trackByPhotoMedia(_index: number, row: { media: any; mediaIndex: number }): string | number {
    const media = row && row.media;
    if (!media) return `photo-${row && row.mediaIndex != null ? row.mediaIndex : _index}`;
    if (media.id != null) return media.id;
    if (media.tempId) return media.tempId;
    if (media.url) return media.url;
    return `photo-${row.mediaIndex != null ? row.mediaIndex : _index}`;
  }

  /** Chunk photo rows for virtual-scroll viewport */
  getPhotoMediaRows(subsection: any): { media: any; mediaIndex: number }[][] {
    const photos = this.getPhotoRows(subsection);
    const rows: { media: any; mediaIndex: number }[][] = [];
    for (let i = 0; i < photos.length; i += PHOTO_GRID_COLUMNS) {
      rows.push(photos.slice(i, i + PHOTO_GRID_COLUMNS));
    }
    return rows;
  }

  trackByPhotoMediaRow(index: number, row: { media: any; mediaIndex: number }[]): string {
    const first = row && row.length > 0 ? row[0] : null;
    const firstMedia = first ? first.media : null;
    const firstId = firstMedia
      ? firstMedia.tempId || firstMedia.id || firstMedia.url || `m-${first.mediaIndex}`
      : `row-${index}`;
    return `row-${index}-${firstId}`;
  }

  usePhotoVirtualScroll(subsection: any): boolean {
    return this.getPhotoRows(subsection).length > VIRTUAL_SCROLL_THRESHOLD;
  }

  getPhotoViewportHeight(): number {
    if (typeof window === "undefined") return 300;
    return Math.min(400, Math.max(250, window.innerHeight * 0.35));
  }

  getImageCountInSection(sectionIndex: number): number {
    const section = this.model.mediaSections && this.model.mediaSections[sectionIndex];
    if (!section) return 0;
    return (section.subSections || []).reduce((sum, sub) => {
      const media = sub.media || [];
      return sum + media.filter(item => this.isPhotoLikeMedia(item)).length;
    }, 0);
  }

  getVideoCountInSection(sectionIndex: number): number {
    const section = this.model.mediaSections && this.model.mediaSections[sectionIndex];
    if (!section) return 0;
    return (section.subSections || []).reduce((sum, sub) => {
      const media = sub.media || [];
      return sum + media.filter(item => this.isVideoMedia(item)).length;
    }, 0);
  }

  validateAndShowErrors(): boolean {
    const errors: string[] = [];
    (this.model.mediaSections || []).forEach((section, index) => {
      const imageCount = this.getImageCountInSection(index);
      const videoCount = this.getVideoCountInSection(index);
      if (imageCount > this.MAX_IMAGES_PER_SECTION) {
        errors.push(`Section ${index + 1}: ${imageCount}/${this.MAX_IMAGES_PER_SECTION} photos (Max exceeded)`);
      }
      if (videoCount > this.MAX_VIDEOS_PER_SECTION) {
        errors.push(`Section ${index + 1}: ${videoCount}/${this.MAX_VIDEOS_PER_SECTION} videos (Max exceeded)`);
      }
    });

    if (errors.length > 0) {
      errors.forEach(error => this.toastr.error(error, "Cannot Submit - Media Limit Exceeded"));
      return false;
    }

    return true;
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }

  onPreviewClick(media: any): void {
    if (media.url) {
      window.open(media.url, "_blank");
    }
  }

  onPhotoImageLoad(media: any): void {
    if (!media) return;
    media.imageLoading = false;
    this.modelChange.emit(this.model);
  }

  onPhotoImageError(media: any): void {
    if (!media) return;
    media.imageLoading = false;
    this.modelChange.emit(this.model);
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  scrollToMediaSection(sectionIndex: number): void {
    const el = document.getElementById(`media-section-${sectionIndex}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  private generateUploadKey(prefix: string, fileName: string, suffix?: string): string {
    const base = `${prefix}_${Date.now()}_${fileName}`;
    return suffix ? `${base}_${suffix}` : base;
  }

  private setUploadState(key: string, isUploading: boolean) {
    this.uploadStates[key] = isUploading;
  }

  private setUploadProgress(key: string, percent: number) {
    this.uploadProgress[key] = percent;
  }

  private cleanupUpload(key: string) {
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  private waitMs(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private generateThumbnailFromUploadedUrl(videoUrl: string, subsection: any, mediaItem: any): void {
    if (!videoUrl || this.isBlobUrl(videoUrl)) {
      return;
    }
    this.videoThumbnailService.generateThumbnailFromVideoUrl(videoUrl, "digital/thumb", 5).subscribe({
      next: (response: any) => {
        if (!response || response.type !== "complete") {
          return;
        }
        const index = this.findVideoMediaIndex(subsection, mediaItem);
        if (index === -1) {
          return;
        }
        if (response.url) {
          subsection.media[index] = {
            ...subsection.media[index],
            thumbnail: response.url,
            thumbnailProcessing: false,
            needsThumbnailRetry: false,
          };
        } else {
          subsection.media[index] = {
            ...subsection.media[index],
            thumbnailProcessing: false,
            needsThumbnailRetry: true,
          };
        }
        this.modelChange.emit(this.model);
      },
      error: () => {
        const index = this.findVideoMediaIndex(subsection, mediaItem);
        if (index === -1) {
          return;
        }
        subsection.media[index] = {
          ...subsection.media[index],
          thumbnailProcessing: false,
          needsThumbnailRetry: true,
        };
        this.modelChange.emit(this.model);
      },
    });
  }

  private retryMissingVideoThumbnails(): void {
    if (document.visibilityState !== "visible" || !this.model || !this.model.mediaSections) {
      return;
    }
    this.model.mediaSections.forEach((section: any) => {
      (section.subSections || []).forEach((subsection: any) => {
        (subsection.media || []).forEach((media: any) => {
          if (!this.isVideoMedia(media) || !this.hasPlayableVideoUrl(media) || this.isBlobUrl(media.url)) {
            return;
          }
          if (media.thumbnailProcessing || this.hasDisplayableThumbnail(media)) {
            return;
          }
          this.ensureMediaClientKeyForRow(media);
          void this.ensureThumbnailFromUploadedUrl(media.url, media.fileName, media.mediaClientKey, false);
        });
      });
    });
  }

  private ensureMediaClientKeyForRow(media: any): void {
    if (!media || media.mediaClientKey) {
      return;
    }
    if (media.id != null && String(media.id).trim() !== "" && Number(media.id) > 0) {
      media.mediaClientKey = `mc_srv_${media.id}`;
      return;
    }
    const url = media.url && typeof media.url === "string" ? media.url : "";
    const fileName = media.fileName && typeof media.fileName === "string" ? media.fileName : "";
    media.mediaClientKey = `mc_legacy_${this.shortHash(url + "::" + fileName)}`;
  }

  private shortHash(value: string): string {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = (Math.imul(31, hash) + value.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36);
  }

  private thumbnailFromUrlDedupeKey(videoUrl: string, mediaClientKey?: string, fileName?: string): string {
    return mediaClientKey ? `k:${mediaClientKey}` : `u:${videoUrl}::${fileName || ""}`;
  }

  private async ensureThumbnailFromUploadedUrl(
    videoUrl: string,
    fileName?: string,
    mediaClientKey?: string,
    showToast: boolean = false,
    attempt: number = 1
  ): Promise<void> {
    if (!videoUrl || this.isBlobUrl(videoUrl)) {
      return;
    }
    const dedupeKey = this.thumbnailFromUrlDedupeKey(videoUrl, mediaClientKey, fileName);
    if (this.thumbnailFromUrlInFlight.has(dedupeKey)) {
      return;
    }
    this.thumbnailFromUrlInFlight.add(dedupeKey);

    const complete = () => {
      this.thumbnailFromUrlInFlight.delete(dedupeKey);
    };

    return new Promise<void>(resolve => {
      let settled = false;
      const finalize = () => {
        if (settled) {
          return;
        }
        settled = true;
        complete();
        resolve();
      };

      const scheduleRetryIfNeeded = () => {
        const canRetry = attempt < 3;
        if (!canRetry) {
          finalize();
          return;
        }
        const delayMs = attempt === 1 ? 1200 : 2200;
        setTimeout(() => {
          void this.ensureThumbnailFromUploadedUrl(videoUrl, fileName, mediaClientKey, showToast, attempt + 1);
          finalize();
        }, delayMs);
      };

      this.videoThumbnailService.generateThumbnailFromVideoUrl(videoUrl, "digital/thumb", 5).subscribe({
        next: (response: any) => {
          if (!response) {
            return;
          }
          if (response.type !== "complete") {
            return;
          }
          let generatedThumbnail: string | null = null;
          if (typeof response.url === "string" && response.url.trim() !== "") {
            generatedThumbnail = response.url;
          } else if (typeof response.s3Url === "string" && response.s3Url.trim() !== "") {
            generatedThumbnail = response.s3Url;
          }
          this.model.mediaSections.forEach((section: any) => {
            (section.subSections || []).forEach((subsection: any) => {
              const index = this.findVideoMediaIndex(subsection, { mediaClientKey, fileName, url: videoUrl });
              if (index === -1) {
                return;
              }
              const current = subsection.media[index];
              if (generatedThumbnail) {
                subsection.media[index] = {
                  ...current,
                  thumbnail: generatedThumbnail,
                  thumbnailProcessing: false,
                  needsThumbnailRetry: false,
                };
                if (showToast) {
                  this.toastr.success(`Thumbnail for ${current.fileName || "video"} generated successfully`);
                }
              } else {
                subsection.media[index] = {
                  ...current,
                  thumbnailProcessing: false,
                  needsThumbnailRetry: true,
                };
              }
            });
          });
          this.modelChange.emit(this.model);
          if (generatedThumbnail) {
            finalize();
            return;
          }
          scheduleRetryIfNeeded();
        },
        error: () => {
          this.model.mediaSections.forEach((section: any) => {
            (section.subSections || []).forEach((subsection: any) => {
              const index = this.findVideoMediaIndex(subsection, { mediaClientKey, fileName, url: videoUrl });
              if (index === -1) {
                return;
              }
              subsection.media[index] = {
                ...subsection.media[index],
                thumbnailProcessing: false,
                needsThumbnailRetry: true,
              };
            });
          });
          if (showToast) {
            this.toastr.warning(
              `Could not generate thumbnail for ${fileName || "video"}. If this is an uploaded URL, please ensure CORS is enabled on the video host.`
            );
          }
          this.modelChange.emit(this.model);
          scheduleRetryIfNeeded();
        },
        complete: () => {
          // Some flows may complete without `type: complete`; do not leave media stuck in processing state.
          this.model.mediaSections.forEach((section: any) => {
            (section.subSections || []).forEach((subsection: any) => {
              const index = this.findVideoMediaIndex(subsection, { mediaClientKey, fileName, url: videoUrl });
              if (index === -1) {
                return;
              }
              const media = subsection.media[index];
              if (!this.hasDisplayableThumbnail(media)) {
                subsection.media[index] = {
                  ...media,
                  thumbnailProcessing: false,
                  needsThumbnailRetry: true,
                };
              }
            });
          });
          this.modelChange.emit(this.model);
          finalize();
        },
      });
    });
  }

  private removePendingMediaWithoutUrl(): void {
    if (!this.model || !this.model.mediaSections) {
      return;
    }
    this.model.mediaSections.forEach(section => {
      if (!section || !section.subSections) {
        return;
      }
      section.subSections.forEach(subsection => {
        if (!subsection || !subsection.media) {
          return;
        }
        subsection.media = subsection.media.filter((media: any) => {
          if (!media) {
            return false;
          }
          const hasUrl = !!media.url;
          const isPending = media.isPending === true;
          return hasUrl || !isPending;
        });
      });
    });
  }
}
